import React, { useState } from "react";
import PropTypes from "prop-types";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import { Helmet } from "react-helmet";
import { SearchProvider, WithSearch } from "@elastic/react-search-ui";
import "@elastic/react-search-ui-views/lib/styles/styles.css";

import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import Link from "@mui/material/Link";
import Card from "@mui/material/Card";
import Snackbar from "@mui/material/Snackbar";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import LinkIcon from "@mui/icons-material/Link";
import CircularProgress from "@mui/material/CircularProgress";
import { getUrl } from "aws-amplify/storage";

import Footer from "./Footer";
import FirearmDetailSections from "./AccordionDetailsContent";
import NotFoundPage from "./NotFoundPage";

import { createFirearmConfig } from "../searchui/createSearchConfig";
import { replaceNextWrite } from "../searchui/firearmUrlSync";
import { extractFrn, copyFirearmLink, buildFirearmSlug } from "../utils/firearmLink";

const PageWrap = styled("div")({
  display: "flex",
  flexDirection: "column",
  minHeight: "100vh",
});

const PageBody = styled("div")(({ theme }) => ({
  maxWidth: 900,
  width: "100%",
  margin: "0 auto",
  padding: theme.spacing(2.5, 3, 4, 3),
}));

const DetailCard = styled(Card)(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  "@media (max-width: 649px)": {
    marginInline: theme.spacing(-3),
    borderLeft: 0,
    borderRight: 0,
    borderRadius: 0,
  },
}));

const CardHeader = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(0.5),
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const TitleStack = styled("div")({
  flex: 1,
  minWidth: 0,
});

const ActionsGroup = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column-reverse",
  alignItems: "center",
  gap: theme.spacing(0.25),
  flexShrink: 0,
  "@media (min-width: 650px)": {
    flexDirection: "row",
    gap: theme.spacing(0.5),
  },
}));

const CardBody = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2.5, 3, 2.5),
}));

const CenteredFallback = styled(PageBody)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: theme.spacing(2),
  paddingBlock: theme.spacing(8),
}));

function FirearmDetailLayout({ result }) {
  const navigate = useNavigate();
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState("");

  const showMsg = (m) => {
    setSnackMsg(m);
    setSnackOpen(true);
  };

  const handleSnackClose = (_e, reason) => {
    if (reason === "clickaway") return;
    setSnackOpen(false);
  };

  const slug = buildFirearmSlug({
    frn: result.frn.raw,
    make: result.make?.raw,
    model: result.model?.raw,
  });
  const firearmQS = `firearm=${encodeURIComponent(slug)}`;
  const goToFirearms = () => navigate(`/?${firearmQS}`);
  const goToMake = () => {
    const makeValue = encodeURIComponent(result.make.raw);
    const makeFilterQS =
      `filters%5B0%5D%5Bfield%5D=make` +
      `&filters%5B0%5D%5Bvalues%5D%5B0%5D=${makeValue}` +
      `&filters%5B0%5D%5Btype%5D=any`;
    // Elastic's follow-up push (after HomePage applies the filter) should
    // replace this navigation entry, not add a second one.
    replaceNextWrite();
    navigate(`/?${makeFilterQS}&${firearmQS}`);
  };

  const frn = result.frn.raw;
  const vd = result.valid_date.raw;
  const s3Key = `${vd}/frn-${frn}_valid-${vd}.pdf`;

  async function downloadPDF() {
    const path = `public/${s3Key}`;
    try {
      const { url } = await getUrl({
        path,
        options: { expiresIn: 180, validateObjectExistence: true },
      });
      const a = document.createElement("a");
      a.href = url.toString();
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (e) {
      console.error("PDF download failed", e);
      showMsg("PDF download failed");
    }
  }

  async function handleCopyLink() {
    try {
      await copyFirearmLink(result);
      showMsg("Link copied");
    } catch {
      showMsg("Could not copy link");
    }
  }

  return (
    <PageBody>
      <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "0.875rem" }}>
        <Link component="button" underline="hover" onClick={goToFirearms} sx={{ fontSize: "0.875rem" }}>
          Firearms
        </Link>
        <Link component="button" underline="hover" onClick={goToMake} sx={{ fontSize: "0.875rem" }}>
          {result.make.raw}
        </Link>
        <Typography color="text.primary" sx={{ fontSize: "0.875rem" }}>
          {result.model.raw}
        </Typography>
      </Breadcrumbs>

      <DetailCard variant="outlined">
        <CardHeader>
          <TitleStack>
            <Typography
              component="h1"
              sx={{ lineHeight: 1.3, fontSize: "1.25rem", wordBreak: "break-word" }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: "text.secondary", mr: 0.5 }}>
                Model:
              </Box>
              <Box component="span" sx={{ fontWeight: 600 }}>
                {result.model.raw}
              </Box>
            </Typography>
            <Typography sx={{ fontSize: "0.9375rem", mt: 0.25 }}>
              <Box component="span" sx={{ fontWeight: 600, color: "text.secondary", mr: 0.5 }}>
                Make:
              </Box>
              <Box component="span" sx={{ fontWeight: 600, color: "primary.main" }}>
                {result.make.raw}
              </Box>
            </Typography>
          </TitleStack>
          <ActionsGroup>
            <Tooltip title="Copy link">
              <IconButton size="small" onClick={handleCopyLink}>
                <LinkIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download PDF">
              <IconButton size="small" onClick={downloadPDF}>
                <GetAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </ActionsGroup>
        </CardHeader>
        <CardBody>
          <FirearmDetailSections result={result} />
        </CardBody>
      </DetailCard>

      <Helmet>
        <title>{`${result.model.raw} | ${result.make.raw} | Armalytics`}</title>
      </Helmet>

      <Snackbar
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        open={snackOpen}
        autoHideDuration={3000}
        onClose={handleSnackClose}
        message={snackMsg}
        action={
          <IconButton size="small" color="inherit" onClick={handleSnackClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        }
      />
    </PageBody>
  );
}

FirearmDetailLayout.propTypes = {
  result: PropTypes.object,
};

function FirearmLoading() {
  return (
    <CenteredFallback>
      <CircularProgress size={32} />
      <Typography variant="body2" color="text.secondary">
        Loading firearm…
      </Typography>
    </CenteredFallback>
  );
}

function FirearmMissing({ frn }) {
  const navigate = useNavigate();
  return (
    <CenteredFallback>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>
        Firearm not found
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ textAlign: "center", maxWidth: 480 }}>
        No firearm with FRN <strong>{frn}</strong> was found in the reference table.
      </Typography>
      <Link component="button" underline="hover" onClick={() => navigate("/")}>
        ← Back to the firearms reference table
      </Link>
    </CenteredFallback>
  );
}

FirearmMissing.propTypes = {
  frn: PropTypes.string,
};

export default function FirearmPage() {
  const { slug } = useParams();
  const frn = extractFrn(slug);
  const { dispatchSI } = useOutletContext() || {};

  if (!frn) {
    return <NotFoundPage />;
  }

  return (
    <SearchProvider config={createFirearmConfig(frn)}>
      <PageWrap>
        <Box sx={{ flex: 1, display: "flex", flexDirection: "column" }}>
          <WithSearch mapContextToProps={({ wasSearched, results, isLoading }) => ({
            wasSearched, results, isLoading,
          })}>
            {({ wasSearched, results, isLoading }) => {
              if (isLoading || !wasSearched) return <FirearmLoading />;
              const result = results?.[0];
              if (!result) return <FirearmMissing frn={frn} />;
              return <FirearmDetailLayout result={result} />;
            }}
          </WithSearch>
        </Box>
        <Footer dispatchSI={dispatchSI} />
      </PageWrap>
    </SearchProvider>
  );
}
